import { useState, useMemo, useCallback, useEffect, useRef } from 'react';
import DashboardLayout from '@/components/layout/DashboardLayout';
import { leadsApi, dealsApi } from '@/services/api';
import { useApiData } from '@/hooks/useApiData';
import { activityFeed, simulatedEvents, type ActivityEvent } from '@/mocks/dashboard';
import { useLocation } from 'react-router-dom';

type CRMSubTab = 'pipeline' | 'leads' | 'analytics';

const subTabs: { key: CRMSubTab; label: string; icon: string }[] = [
  { key: 'pipeline', label: 'Pipeline', icon: 'ri-kanban-view' },
  { key: 'leads', label: 'Lead Scoring', icon: 'ri-bar-chart-grouped-line' },
  { key: 'analytics', label: 'Analytics', icon: 'ri-pie-chart-line' },
];

interface InquiryRow {
  id: number;
  name: string;
  email: string;
  phone: string;
  company: string | null;
  subject: string;
  message: string;
  source: string;
  status: string;
  listing_title: string | null;
  notes: string | null;
  created_at: string;
}

const pipelineStages = ['new', 'contacted', 'qualified', 'site_visit', 'negotiation', 'converted', 'lost', 'captured'];

const stageLabels: Record<string, string> = {
  new: 'New Lead', contacted: 'Contacted', qualified: 'Qualified',
  site_visit: 'Site Visit', negotiation: 'Negotiation', converted: 'Converted',
  lost: 'Lost', captured: 'Captured',
  in_progress: 'In Progress', closed: 'Closed Won', spam: 'Closed Lost',
};

const stageColors: Record<string, string> = {
  new: 'border-l-blue-500', contacted: 'border-l-accent-500',
  qualified: 'border-l-teal-500', site_visit: 'border-l-secondary-500',
  negotiation: 'border-l-primary-500', converted: 'border-l-green-500',
  lost: 'border-l-rose-500', captured: 'border-l-foreground-500',
};

const statusBadge = (status: string) => {
  const map: Record<string, string> = {
    new: 'bg-blue-500/15 text-blue-400 border-blue-500/25',
    contacted: 'bg-accent-100 text-accent-700 border-accent-500/25',
    qualified: 'bg-teal-500/15 text-teal-400 border-teal-500/25',
    site_visit: 'bg-secondary-100 text-secondary-700 border-secondary-200',
    negotiation: 'bg-primary-100 text-primary-700 border-primary-200',
    converted: 'bg-green-500/15 text-green-400 border-green-500/25',
    lost: 'bg-rose-500/15 text-rose-400 border-rose-500/25',
    captured: 'bg-background-200/50 text-foreground-500 border-background-300/30',
  };
  return map[status] || 'bg-background-200/50 text-foreground-500 border-background-300/30';
};

function formatDate(d: string) {
  if (!d) return '-';
  return new Date(d).toLocaleDateString('en-IN', { day: 'numeric', month: 'short', year: 'numeric' });
}

function formatINR(val: number): string {
  if (val >= 10000000) return '\u20B9' + (val / 10000000).toFixed(1) + 'Cr';
  if (val >= 100000) return '\u20B9' + (val / 100000).toFixed(1) + 'L';
  if (val >= 1000) return '\u20B9' + (val / 1000).toFixed(0) + 'K';
  return '\u20B9' + val.toFixed(0);
}

const allSources = ['Website', 'WhatsApp', 'Referral', 'Instagram', 'Google Ads', 'Phone Call', 'Facebook', 'LinkedIn'];
const sortOptions = [
  { key: 'name', label: 'Name' },
  { key: 'date', label: 'Date' },
  { key: 'score', label: 'Score' },
  { key: 'value', label: 'Value' },
];

const emptyLeadForm = { name: '', email: '', phone: '', source: 'website', subject: '', message: '', listing_title: '' };

export default function CRMPage() {
  const location = useLocation();
  const dashboardFilter = (location.state as Record<string, unknown> | null)?.dashboardFilter as Record<string, string> | undefined;

  const [activeTab, setActiveTab] = useState<CRMSubTab>(dashboardFilter?.tab === 'analytics' ? 'analytics' : 'pipeline');
  const [selectedLead, setSelectedLead] = useState<InquiryRow | null>(null);
  const [statusNotes, setStatusNotes] = useState('');
  const [deleteTarget, setDeleteTarget] = useState<InquiryRow | null>(null);
  const [actionMsg, setActionMsg] = useState<{ type: 'success' | 'error'; text: string } | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const [showCreate, setShowCreate] = useState(false);
  const [createForm, setCreateForm] = useState(emptyLeadForm);
  const [refreshKey, setRefreshKey] = useState(0);

  // --- Activity Feed State ---
  const [activityEvents, setActivityEvents] = useState<ActivityEvent[]>(activityFeed);
  const [feedOpen, setFeedOpen] = useState(true);
  const [pulseNew, setPulseNew] = useState(false);
  const simIndexRef = useRef(0);
  const idCounterRef = useRef(100);

  useEffect(() => {
    const interval = setInterval(() => {
      const nextEvent = simulatedEvents[simIndexRef.current % simulatedEvents.length];
      simIndexRef.current += 1;
      idCounterRef.current += 1;
      const newEvent: ActivityEvent = {
        ...nextEvent,
        id: idCounterRef.current,
        timestamp: new Date().toISOString(),
      };
      setActivityEvents(prev => [newEvent, ...prev].slice(0, 50));
      setPulseNew(true);
      setTimeout(() => setPulseNew(false), 2000);
    }, 12000 + Math.random() * 8000);

    return () => clearInterval(interval);
  }, []);

  const getRelativeTime = (ts: string) => {
    const diff = Date.now() - new Date(ts).getTime();
    const secs = Math.floor(diff / 1000);
    if (secs < 60) return `${secs}s ago`;
    const mins = Math.floor(secs / 60);
    if (mins < 60) return `${mins}m ago`;
    const hrs = Math.floor(mins / 60);
    if (hrs < 24) return `${hrs}h ago`;
    const days = Math.floor(hrs / 24);
    return `${days}d ago`;
  };

  const eventIcon = (type: ActivityEvent['type']) => {
    const map: Record<string, string> = {
      lead_created: 'ri-user-add-line',
      lead_status_change: 'ri-arrow-up-circle-line',
      lead_scored: 'ri-bar-chart-line',
      property_update: 'ri-home-5-line',
      property_listed: 'ri-building-line',
      deal_movement: 'ri-arrow-right-circle-line',
      deal_closed: 'ri-trophy-line',
      inquiry_received: 'ri-mail-line',
      reminder_due: 'ri-alarm-line',
    };
    return map[type] || 'ri-notification-3-line';
  };

  const eventColor = (type: ActivityEvent['type']) => {
    const map: Record<string, string> = {
      lead_created: 'bg-accent-500',
      lead_status_change: 'bg-accent-500',
      lead_scored: 'bg-primary-500',
      property_update: 'bg-teal-500',
      property_listed: 'bg-teal-500',
      deal_movement: 'bg-secondary-500',
      deal_closed: 'bg-green-500',
      inquiry_received: 'bg-blue-500',
      reminder_due: 'bg-rose-500',
    };
    return map[type] || 'bg-foreground-500';
  };

  // --- Filter & Sort State ---
  const [filterSource, setFilterSource] = useState<string>('');
  const [filterStatus, setFilterStatus] = useState<string>('');
  const [filterDateFrom, setFilterDateFrom] = useState<string>('');
  const [filterDateTo, setFilterDateTo] = useState<string>('');
  const [searchQuery, setSearchQuery] = useState<string>('');
  const [showFilters, setShowFilters] = useState(false);
  const [sortBy, setSortBy] = useState<string>('date');
  const [sortAsc, setSortAsc] = useState(false);

  const { data: leadsApiResp, loading } = useApiData<{ data: InquiryRow[]; pagination: { total: number } }>(
    () => leadsApi.list({ per_page: 100, page: 1 }) as unknown as Promise<{ data: InquiryRow[]; pagination: { total: number } }>,
    [refreshKey]
  );

  const { data: dealsApiResp } = useApiData<{ data: Record<string, unknown>[] }>(
    () => dealsApi.list({ per_page: 50 }) as unknown as Promise<{ data: Record<string, unknown>[] }>,
    [refreshKey]
  );

  const allLeads = useMemo(() => {
    return (leadsApiResp?.data || []) as InquiryRow[];
  }, [leadsApiResp]);

  const apiDeals = useMemo(() => dealsApiResp?.data || [], [dealsApiResp]);

  // Apply filters
  const filteredLeads = useMemo(() => {
    let result = [...allLeads];

    if (filterSource) {
      result = result.filter(l => l.source?.toLowerCase() === filterSource.toLowerCase());
    }
    if (filterStatus) {
      result = result.filter(l => l.status === filterStatus);
    }
    if (filterDateFrom) {
      result = result.filter(l => new Date(l.created_at) >= new Date(filterDateFrom));
    }
    if (filterDateTo) {
      result = result.filter(l => new Date(l.created_at) <= new Date(filterDateTo + 'T23:59:59'));
    }
    if (searchQuery) {
      const q = searchQuery.toLowerCase();
      result = result.filter(l =>
        l.name.toLowerCase().includes(q) ||
        l.email.toLowerCase().includes(q) ||
        (l.listing_title || '').toLowerCase().includes(q) ||
        (l.subject || '').toLowerCase().includes(q)
      );
    }

    return result;
  }, [allLeads, filterSource, filterStatus, filterDateFrom, filterDateTo, searchQuery]);

  // Apply sorting
  const sortedLeads = useMemo(() => {
    return [...filteredLeads].sort((a, b) => {
      const extendedA = a as InquiryRow & { _score?: number; _value?: number };
      const extendedB = b as InquiryRow & { _score?: number; _value?: number };
      let cmp = 0;
      switch (sortBy) {
        case 'name':
          cmp = a.name.localeCompare(b.name);
          break;
        case 'date':
          cmp = new Date(a.created_at).getTime() - new Date(b.created_at).getTime();
          break;
        case 'score':
          cmp = (extendedA._score || 0) - (extendedB._score || 0);
          break;
        case 'value':
          cmp = (extendedA._value || 0) - (extendedB._value || 0);
          break;
        default:
          cmp = 0;
      }
      return sortAsc ? cmp : -cmp;
    });
  }, [filteredLeads, sortBy, sortAsc]);

  const pipelineCounts = useMemo(() => pipelineStages.map(stage => ({
    stage: stageLabels[stage],
    rawStage: stage,
    count: sortedLeads.filter(l => l.status === stage).length,
    items: sortedLeads.filter(l => l.status === stage),
  })), [sortedLeads]);

  const totalLeads = sortedLeads.length;
  const converted = sortedLeads.filter(l => l.status === 'converted' || l.status === 'closed').length;
  const winRate = totalLeads > 0 ? Math.round((converted / totalLeads) * 100) : 0;

  const hasActiveFilters = filterSource || filterStatus || filterDateFrom || filterDateTo || searchQuery;

  const clearFilters = () => {
    setFilterSource('');
    setFilterStatus('');
    setFilterDateFrom('');
    setFilterDateTo('');
    setSearchQuery('');
  };

  const clearMsg = () => setTimeout(() => setActionMsg(null), 3000);

  const handleStatusUpdate = useCallback(async (leadId: number, newStatus: string) => {
    setSubmitting(true);
    try {
      await leadsApi.updateStage(leadId, newStatus, statusNotes || undefined);
      setActionMsg({ type: 'success', text: `Status updated to "${stageLabels[newStatus] || newStatus}".` });
      setStatusNotes('');
      setRefreshKey(k => k + 1);
      setSelectedLead(prev => prev && prev.id === leadId ? { ...prev, status: newStatus } : prev);
    } catch (err) {
      setActionMsg({ type: 'error', text: err instanceof Error ? err.message : 'Update failed.' });
    } finally {
      setSubmitting(false);
      clearMsg();
    }
  }, [statusNotes]);

  const handleDelete = useCallback(async () => {
    if (!deleteTarget) return;
    setSubmitting(true);
    try {
      await leadsApi.delete(deleteTarget.id);
      setActionMsg({ type: 'success', text: `Lead "${deleteTarget.name}" deleted.` });
      setDeleteTarget(null);
      setSelectedLead(null);
      setRefreshKey(k => k + 1);
    } catch (err) {
      setActionMsg({ type: 'error', text: err instanceof Error ? err.message : 'Delete failed.' });
    } finally {
      setSubmitting(false);
      clearMsg();
    }
  }, [deleteTarget]);

  const handleCreateLead = useCallback(async () => {
    if (!createForm.name) {
      setActionMsg({ type: 'error', text: 'Name is required.' });
      clearMsg();
      return;
    }
    setSubmitting(true);
    try {
      await leadsApi.create({
        name: createForm.name,
        email: createForm.email || undefined,
        phone: createForm.phone || undefined,
        source: createForm.source || 'website',
        property_interest: createForm.listing_title || createForm.subject || undefined,
        notes: createForm.message || undefined,
      });
      setActionMsg({ type: 'success', text: `Lead "${createForm.name}" created successfully!` });
      setShowCreate(false);
      setCreateForm(emptyLeadForm);
      setRefreshKey(k => k + 1);
    } catch (err) {
      setActionMsg({ type: 'error', text: err instanceof Error ? err.message : 'Failed to create lead.' });
    } finally {
      setSubmitting(false);
      clearMsg();
    }
  }, [createForm]);

  return (
    <DashboardLayout>
      <div className="space-y-5">
        {/* Toast */}
        {actionMsg && (
          <div className={`fixed top-4 right-4 z-[1100] px-4 py-3 rounded-xl text-sm font-semibold shadow-lg ${actionMsg.type === 'success' ? 'bg-teal-500/20 text-teal-400 border border-teal-500/30' : 'bg-rose-500/20 text-rose-400 border border-rose-500/30'}`}>
            {actionMsg.text}
          </div>
        )}

        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
          <div>
            <h1 className="font-heading text-xl font-bold text-foreground-950">CRM</h1>
            <p className="text-xs text-foreground-500">Customer Relationship Management{loading ? ' (loading...)' : ` — ${totalLeads} leads`}</p>
          </div>
          <div className="flex items-center gap-2">
            <button
              onClick={() => setFeedOpen(!feedOpen)}
              className={`relative flex items-center gap-1.5 px-3 py-2.5 rounded-xl text-sm font-semibold cursor-pointer whitespace-nowrap transition-all ${
                feedOpen ? 'bg-accent-100 text-accent-700 border border-accent-200' : 'bg-white border border-background-200/60 text-foreground-500 hover:text-foreground-950'
              }`}
            >
              <i className="ri-live-line" />
              Live Feed
              {pulseNew && <span className="absolute -top-1 -right-1 w-2.5 h-2.5 rounded-full bg-rose-500 animate-ping" />}
              {pulseNew && <span className="absolute -top-1 -right-1 w-2.5 h-2.5 rounded-full bg-rose-500" />}
            </button>
            <button onClick={() => { setCreateForm(emptyLeadForm); setShowCreate(true); }} className="flex items-center gap-1.5 px-4 py-2.5 rounded-xl bg-accent-500 text-white text-sm font-semibold hover:bg-accent-400 cursor-pointer whitespace-nowrap">
              <i className="ri-user-add-line" /> Add Lead
            </button>
          </div>
        </div>

        {/* Sub-tabs */}
        <div className="flex flex-wrap items-center gap-3">
          <div className="flex items-center gap-1 bg-background-50 rounded-xl p-1">
            {subTabs.map(tab => (
              <button key={tab.key} onClick={() => setActiveTab(tab.key)} className={`flex items-center gap-1.5 px-4 py-2 rounded-lg text-sm whitespace-nowrap cursor-pointer transition-all ${
                activeTab === tab.key ? 'bg-accent-100 text-accent-700 font-semibold' : 'text-foreground-500 hover:text-foreground-950'
              }`}>
                <i className={tab.icon} />{tab.label}
              </button>
            ))}
          </div>

          {/* Sort controls */}
          <div className="flex items-center gap-2 ml-auto flex-shrink-0">
            <span className="text-[10px] text-foreground-500 uppercase tracking-wider">Sort:</span>
            <select
              value={sortBy}
              onChange={e => setSortBy(e.target.value)}
              className="px-2.5 py-1.5 rounded-lg bg-white border border-background-200/60 text-xs text-foreground-950 cursor-pointer"
            >
              {sortOptions.map(o => (
                <option key={o.key} value={o.key}>{o.label}</option>
              ))}
            </select>
            <button
              onClick={() => setSortAsc(!sortAsc)}
              className="w-7 h-7 flex items-center justify-center rounded-lg bg-white border border-background-200/60 text-foreground-500 hover:text-foreground-950 cursor-pointer transition-colors"
              title={sortAsc ? 'Ascending' : 'Descending'}
            >
              <i className={`text-xs ${sortAsc ? 'ri-sort-asc' : 'ri-sort-desc'}`} />
            </button>
            <button
              onClick={() => setShowFilters(!showFilters)}
              className={`flex items-center gap-1 px-3 py-1.5 rounded-lg text-xs font-semibold cursor-pointer whitespace-nowrap transition-all ${
                hasActiveFilters || showFilters
                  ? 'bg-accent-100 text-accent-700 border border-accent-200'
                  : 'bg-white border border-background-200/60 text-foreground-500 hover:text-foreground-950'
              }`}
            >
              <i className="ri-filter-3-line text-[11px]" />
              Filters
              {hasActiveFilters && (
                <span className="w-4 h-4 rounded-full bg-accent-500 text-white text-[9px] flex items-center justify-center font-bold">
                  {[filterSource, filterStatus, filterDateFrom, searchQuery].filter(Boolean).length}
                </span>
              )}
            </button>
          </div>
        </div>

        {/* Filter Panel */}
        {showFilters && (
          <div className="rounded-xl border border-accent-200 bg-white p-4 space-y-3">
            <div className="flex items-center justify-between">
              <span className="text-xs font-semibold text-foreground-950">Filter Leads</span>
              {hasActiveFilters && (
                <button onClick={clearFilters} className="text-[10px] text-rose-400 hover:text-rose-300 cursor-pointer whitespace-nowrap">
                  <i className="ri-close-line" /> Clear all
                </button>
              )}
            </div>
            <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
              <div>
                <label className="text-[10px] font-semibold text-foreground-500 uppercase tracking-wider block mb-1">Source</label>
                <select
                  value={filterSource}
                  onChange={e => setFilterSource(e.target.value)}
                  className="w-full px-3 py-2 rounded-lg bg-white border border-background-200/60 text-xs text-foreground-950 cursor-pointer"
                >
                  <option value="">All Sources</option>
                  {allSources.map(s => <option key={s} value={s}>{s}</option>)}
                </select>
              </div>
              <div>
                <label className="text-[10px] font-semibold text-foreground-500 uppercase tracking-wider block mb-1">Status</label>
                <select
                  value={filterStatus}
                  onChange={e => setFilterStatus(e.target.value)}
                  className="w-full px-3 py-2 rounded-lg bg-white border border-background-200/60 text-xs text-foreground-950 cursor-pointer"
                >
                  <option value="">All Statuses</option>
                  {pipelineStages.map(s => (
                    <option key={s} value={s}>{stageLabels[s]}</option>
                  ))}
                  <option value="lost">Lost</option>
                </select>
              </div>
              <div>
                <label className="text-[10px] font-semibold text-foreground-500 uppercase tracking-wider block mb-1">Date From</label>
                <input
                  type="date"
                  value={filterDateFrom}
                  onChange={e => setFilterDateFrom(e.target.value)}
                  className="w-full px-3 py-2 rounded-lg bg-white border border-background-200/60 text-xs text-foreground-950"
                />
              </div>
              <div>
                <label className="text-[10px] font-semibold text-foreground-500 uppercase tracking-wider block mb-1">Date To</label>
                <input
                  type="date"
                  value={filterDateTo}
                  onChange={e => setFilterDateTo(e.target.value)}
                  className="w-full px-3 py-2 rounded-lg bg-white border border-background-200/60 text-xs text-foreground-950"
                />
              </div>
              <div>
                <label className="text-[10px] font-semibold text-foreground-500 uppercase tracking-wider block mb-1">Search</label>
                <div className="relative">
                  <i className="ri-search-line absolute left-2.5 top-1/2 -translate-y-1/2 text-xs text-foreground-500" />
                  <input
                    type="text"
                    placeholder="Name, email, property..."
                    value={searchQuery}
                    onChange={e => setSearchQuery(e.target.value)}
                    className="w-full pl-7 pr-3 py-2 rounded-lg bg-white border border-background-200/60 text-xs text-foreground-950 placeholder:text-foreground-500"
                  />
                </div>
              </div>
            </div>
            {hasActiveFilters && (
              <p className="text-[10px] text-foreground-500">
                Showing <span className="text-foreground-950 font-semibold">{sortedLeads.length}</span> of {allLeads.length} leads
              </p>
            )}
          </div>
        )}

        {/* Pipeline View */}
        {activeTab === 'pipeline' && (
          <div className="space-y-4">
            <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
              {[
                { label: 'Total Leads', value: totalLeads.toString(), icon: 'ri-user-line', color: 'accent' },
                { label: 'Converted', value: converted.toString(), icon: 'ri-trophy-line', color: 'teal' },
                { label: 'Win Rate', value: winRate + '%', icon: 'ri-line-chart-line', color: 'rose' },
              ].map(stat => (
                <div key={stat.label} className="rounded-xl border bg-white border-background-200/60 p-4">
                  <div className="flex items-center gap-2 mb-2">
                    <span className={`w-7 h-7 rounded-lg flex items-center justify-center ${
                      stat.color === 'accent' ? 'bg-accent-100 text-accent-700' :
                      stat.color === 'teal' ? 'bg-teal-500/15 text-teal-400' : 'bg-rose-500/15 text-rose-400'
                    }`}><i className={`${stat.icon} text-xs`} /></span>
                    <span className="text-[10px] text-foreground-500 uppercase tracking-wider">{stat.label}</span>
                  </div>
                  <p className="font-heading text-lg font-bold text-foreground-950">{stat.value}</p>
                </div>
              ))}
            </div>

            {/* Kanban Columns */}
            {loading ? (
              <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                {Array.from({ length: 3 }).map((_, i) => (
                  <div key={i} className="rounded-xl border border-background-200/60 min-h-[200px] animate-pulse">
                    <div className="h-10 bg-background-200/60 rounded-t-xl" />
                    <div className="p-3 space-y-2">
                      <div className="h-16 bg-background-200/50 rounded-lg" />
                      <div className="h-16 bg-background-200/50 rounded-lg" />
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <div className="grid grid-cols-1 md:grid-cols-3 lg:grid-cols-6 gap-3 overflow-x-auto">
                {pipelineCounts.map(col => (
                  <div key={col.stage} className="rounded-xl border border-background-200/60 bg-background-200 min-w-[180px]">
                    <div className={`px-3 py-2.5 border-b border-background-200/60 flex items-center justify-between ${stageColors[col.rawStage] || ''} border-l-4`}>
                      <span className="text-xs font-semibold text-foreground-950">{col.stage}</span>
                      <span className="text-[10px] text-foreground-500 bg-background-200/60 px-1.5 py-0.5 rounded-full">{col.count}</span>
                    </div>
                    <div className="p-2 space-y-2 min-h-[120px]">
                      {col.items.length === 0 ? (
                        <p className="text-[11px] text-foreground-500 text-center py-4">No leads</p>
                      ) : (
                        col.items.map(lead => {
                          const extended = lead as InquiryRow & { _score?: number; _value?: number; _assignedRM?: string };
                          return (
                            <div key={lead.id} onClick={() => { setSelectedLead(lead); setStatusNotes(''); }} className="p-3 rounded-lg bg-white border border-background-200/60 hover:border-accent-200 cursor-pointer transition-all">
                              <p className="text-sm font-medium text-foreground-950 mb-1">{lead.name}</p>
                              <p className="text-[11px] text-foreground-500 truncate">{lead.listing_title || lead.subject || 'General Inquiry'}</p>
                              <div className="flex items-center justify-between mt-2">
                                <span className={`text-[10px] font-semibold px-1.5 py-0.5 rounded-full border ${statusBadge(lead.status)}`}>{stageLabels[lead.status] || lead.status}</span>
                                <span className="text-[10px] text-foreground-500">{formatDate(lead.created_at)}</span>
                              </div>
                              {extended._score && (
                                <div className="flex items-center gap-2 mt-2 pt-2 border-t border-background-200/50">
                                  <span className="text-[9px] text-foreground-500">Score</span>
                                  <div className="flex-1 h-1 bg-background-200/60 rounded-full overflow-hidden">
                                    <div className={`h-full rounded-full ${(extended._score || 0) >= 75 ? 'bg-teal-500' : (extended._score || 0) >= 50 ? 'bg-accent-500' : 'bg-rose-500'}`} style={{ width: `${extended._score || 0}%` }} />
                                  </div>
                                  <span className="text-[9px] font-semibold text-foreground-950">{extended._score}</span>
                                </div>
                              )}
                            </div>
                          );
                        })
                      )}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}

        {/* Lead Scoring Table */}
        {activeTab === 'leads' && (
          <div className="rounded-xl border border-background-200/60 bg-white overflow-hidden">
            {loading ? (
              <div className="p-12 space-y-3">
                {Array.from({ length: 5 }).map((_, i) => <div key={i} className="h-12 bg-background-200/60 rounded-lg animate-pulse" />)}
              </div>
            ) : sortedLeads.length === 0 ? (
              <div className="p-12 text-center">
                <i className="ri-inbox-line text-3xl text-foreground-500 mb-2 block" />
                <p className="text-sm text-foreground-500">{hasActiveFilters ? 'No leads match your filters.' : 'No leads yet.'}</p>
                {hasActiveFilters && (
                  <button onClick={clearFilters} className="mt-2 text-xs text-accent-700 hover:text-accent-300 cursor-pointer">Clear filters</button>
                )}
              </div>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead>
                    <tr className="border-b border-background-200/60">
                      <th className="text-left text-[10px] font-semibold text-foreground-500 uppercase tracking-wider px-4 py-3">Lead</th>
                      <th className="text-left text-[10px] font-semibold text-foreground-500 uppercase tracking-wider px-4 py-3">Source</th>
                      <th className="text-left text-[10px] font-semibold text-foreground-500 uppercase tracking-wider px-4 py-3">Interest</th>
                      <th className="text-left text-[10px] font-semibold text-foreground-500 uppercase tracking-wider px-4 py-3">Status</th>
                      <th className="text-left text-[10px] font-semibold text-foreground-500 uppercase tracking-wider px-4 py-3">Score</th>
                      <th className="text-left text-[10px] font-semibold text-foreground-500 uppercase tracking-wider px-4 py-3">Value</th>
                      <th className="text-left text-[10px] font-semibold text-foreground-500 uppercase tracking-wider px-4 py-3">Date</th>
                    </tr>
                  </thead>
                  <tbody>
                    {sortedLeads.map(lead => {
                      const extended = lead as InquiryRow & { _score?: number; _value?: number; _assignedRM?: string };
                      return (
                        <tr key={lead.id} onClick={() => { setSelectedLead(lead); setStatusNotes(''); }} className="border-b border-background-200/50 hover:bg-background-200/80 transition-colors cursor-pointer">
                          <td className="px-4 py-3">
                            <p className="text-sm font-medium text-foreground-950">{lead.name}</p>
                            <p className="text-[11px] text-foreground-500">{lead.email}</p>
                          </td>
                          <td className="px-4 py-3">
                            <span className="inline-flex items-center gap-1 text-xs text-foreground-500">
                              <i className="ri-global-line text-accent-700" /> {lead.source || 'website'}
                            </span>
                          </td>
                          <td className="px-4 py-3 text-xs text-foreground-500 max-w-[200px] truncate">{lead.listing_title || lead.subject || 'General Inquiry'}</td>
                          <td className="px-4 py-3">
                            <span className={`text-[10px] font-semibold px-2 py-0.5 rounded-full border ${statusBadge(lead.status)}`}>{stageLabels[lead.status] || lead.status}</span>
                          </td>
                          <td className="px-4 py-3">
                            {extended._score !== undefined ? (
                              <div className="flex items-center gap-2">
                                <div className="w-12 h-1.5 bg-background-200/60 rounded-full overflow-hidden">
                                  <div className={`h-full rounded-full ${extended._score >= 75 ? 'bg-teal-500' : extended._score >= 50 ? 'bg-accent-500' : 'bg-rose-500'}`} style={{ width: `${extended._score}%` }} />
                                </div>
                                <span className="text-[10px] font-semibold text-foreground-950">{extended._score}</span>
                              </div>
                            ) : (
                              <span className="text-[10px] text-foreground-500">-</span>
                            )}
                          </td>
                          <td className="px-4 py-3">
                            <span className="text-xs font-semibold text-foreground-950">{extended._value ? formatINR(extended._value) : '-'}</span>
                          </td>
                          <td className="px-4 py-3 text-xs text-foreground-500 whitespace-nowrap">{formatDate(lead.created_at)}</td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        )}

        {/* Analytics */}
        {activeTab === 'analytics' && (
          <div className="space-y-4">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {/* Leads by Source */}
              <div className="rounded-xl border border-background-200/60 bg-white p-5">
                <h3 className="text-sm font-semibold text-foreground-950 mb-4">Leads by Source</h3>
                <div className="space-y-2">
                  {(() => {
                    const sources = sortedLeads.reduce((acc, l) => {
                      const s = l.source || 'website';
                      acc[s] = (acc[s] || 0) + 1;
                      return acc;
                    }, {} as Record<string, number>);
                    const total = sortedLeads.length || 1;
                    const colors = ['bg-accent-500', 'bg-primary-500', 'bg-secondary-500', 'bg-teal-500', 'bg-rose-500', 'bg-blue-500'];
                    return Object.entries(sources).sort((a, b) => b[1] - a[1]).map(([source, count], i) => (
                      <div key={source} className="flex items-center gap-3">
                        <span className={`w-3 h-3 rounded-sm ${colors[i % colors.length]} flex-shrink-0`} />
                        <span className="text-xs text-foreground-500 flex-1 capitalize">{source}</span>
                        <div className="w-20 h-1.5 bg-background-200/60 rounded-full overflow-hidden">
                          <div className={`h-full rounded-full ${colors[i % colors.length]}`} style={{ width: `${Math.round((count / total) * 100)}%` }} />
                        </div>
                        <span className="text-xs font-semibold text-foreground-950">{count}</span>
                        <span className="text-[10px] text-foreground-500 w-8 text-right">{Math.round((count / total) * 100)}%</span>
                      </div>
                    ));
                  })()}
                  {sortedLeads.length === 0 && <p className="text-xs text-foreground-500 py-4 text-center">No data yet.</p>}
                </div>
              </div>

              {/* Status Breakdown */}
              <div className="rounded-xl border border-background-200/60 bg-white p-5">
                <h3 className="text-sm font-semibold text-foreground-950 mb-4">Status Breakdown</h3>
                <div className="space-y-2">
                  {pipelineCounts.map(col => (
                    <div key={col.stage} className="flex items-center gap-3">
                      <span className={`w-3 h-3 rounded-sm ${col.rawStage === 'new' ? 'bg-blue-500' : col.rawStage === 'contacted' ? 'bg-accent-500' : col.rawStage === 'qualified' ? 'bg-teal-500' : col.rawStage === 'site_visit' ? 'bg-secondary-500' : col.rawStage === 'negotiation' ? 'bg-primary-500' : col.rawStage === 'lost' ? 'bg-rose-500' : 'bg-green-500'} flex-shrink-0`} />
                      <span className="text-xs text-foreground-500 flex-1">{col.stage}</span>
                      <div className="w-20 h-1.5 bg-background-200/60 rounded-full overflow-hidden">
                        <div className={`h-full rounded-full ${col.rawStage === 'new' ? 'bg-blue-500' : col.rawStage === 'contacted' ? 'bg-accent-500' : col.rawStage === 'qualified' ? 'bg-teal-500' : col.rawStage === 'site_visit' ? 'bg-secondary-500' : col.rawStage === 'negotiation' ? 'bg-primary-500' : col.rawStage === 'lost' ? 'bg-rose-500' : 'bg-green-500'}`} style={{ width: `${sortedLeads.length > 0 ? Math.round((col.count / sortedLeads.length) * 100) : 0}%` }} />
                      </div>
                      <span className="text-xs font-semibold text-foreground-950">{col.count}</span>
                      <span className="text-[10px] text-foreground-500 w-8 text-right">{sortedLeads.length > 0 ? Math.round((col.count / sortedLeads.length) * 100) : 0}%</span>
                    </div>
                  ))}
                  {sortedLeads.length === 0 && <p className="text-xs text-foreground-500 py-4 text-center">No data yet.</p>}
                </div>
              </div>
            </div>

            {/* Deal Pipeline Summary */}
            <div className="rounded-xl border border-background-200/60 bg-white p-5">
              <h3 className="text-sm font-semibold text-foreground-950 mb-3">Deal Pipeline ({apiDeals.length} deals)</h3>
              {apiDeals.length === 0 ? (
                <p className="text-xs text-foreground-500 py-4 text-center">No deals yet.</p>
              ) : (
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead>
                    <tr className="border-b border-background-200/60">
                      <th className="text-left text-[10px] font-semibold text-foreground-500 uppercase tracking-wider px-4 py-3">Lead</th>
                      <th className="text-left text-[10px] font-semibold text-foreground-500 uppercase tracking-wider px-4 py-3">Property</th>
                      <th className="text-left text-[10px] font-semibold text-foreground-500 uppercase tracking-wider px-4 py-3">Value</th>
                      <th className="text-left text-[10px] font-semibold text-foreground-500 uppercase tracking-wider px-4 py-3">Stage</th>
                      <th className="text-left text-[10px] font-semibold text-foreground-500 uppercase tracking-wider px-4 py-3">Probability</th>
                      <th className="text-left text-[10px] font-semibold text-foreground-500 uppercase tracking-wider px-4 py-3">Close Date</th>
                    </tr>
                  </thead>
                  <tbody>
                    {apiDeals.map((deal, idx) => (
                      <tr key={String(deal.id) || idx} className="border-b border-background-200/50">
                        <td className="px-4 py-3 text-sm text-foreground-950 font-medium">{String(deal.lead_name || deal.client_name || '-')}</td>
                        <td className="px-4 py-3 text-xs text-foreground-500">{String(deal.property_name || '-')}</td>
                        <td className="px-4 py-3 text-xs font-bold text-foreground-950">{formatINR(Number(deal.deal_value) || 0)}</td>
                        <td className="px-4 py-3">
                          <span className={`text-[10px] font-semibold px-2 py-0.5 rounded-full border ${
                            String(deal.stage).includes('closed_won') || String(deal.stage).includes('paid') ? 'bg-green-500/15 text-green-400 border-green-500/25' :
                            String(deal.stage).includes('agreement') ? 'bg-teal-500/15 text-teal-400 border-teal-500/25' :
                            'bg-accent-100 text-accent-700 border-accent-500/25'
                          }`}>{String(deal.stage || '-').replace(/_/g, ' ')}</span>
                        </td>
                        <td className="px-4 py-3">
                          <div className="flex items-center gap-2">
                            <div className="w-12 h-1.5 bg-background-200/60 rounded-full overflow-hidden">
                              <div className={`h-full rounded-full ${Number(deal.stage_probability) >= 70 ? 'bg-teal-500' : Number(deal.stage_probability) >= 40 ? 'bg-accent-500' : 'bg-rose-500'}`} style={{ width: `${Number(deal.stage_probability) || 0}%` }} />
                            </div>
                            <span className="text-[10px] font-semibold text-foreground-950">{Number(deal.stage_probability) || 0}%</span>
                          </div>
                        </td>
                        <td className="px-4 py-3 text-xs text-foreground-500">{formatDate(String(deal.expected_close_date || ''))}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              )}
            </div>
          </div>
        )}
      </div>

      {/* Activity Feed Panel */}
      {feedOpen && (
        <div className="rounded-xl border border-accent-200 bg-white overflow-hidden">
          <div className="flex items-center justify-between px-5 py-3 border-b border-background-200/60 bg-background-50/70">
            <div className="flex items-center gap-2">
              <span className="relative flex h-2 w-2">
                <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-green-500 opacity-75" />
                <span className="relative inline-flex rounded-full h-2 w-2 bg-green-500" />
              </span>
              <h3 className="text-xs font-semibold text-foreground-950">Live Activity Feed</h3>
              <span className="text-[10px] text-foreground-500">{activityEvents.length} events</span>
            </div>
            <button
              onClick={() => setFeedOpen(false)}
              className="w-7 h-7 flex items-center justify-center rounded-lg hover:bg-background-200/60 text-foreground-500 cursor-pointer"
            >
              <i className="ri-close-line text-sm" />
            </button>
          </div>
          <div className="max-h-[360px] overflow-y-auto">
            {activityEvents.map((event, idx) => (
              <div
                key={event.id}
                className={`flex items-start gap-3 px-5 py-3 border-b border-background-100 last:border-0 transition-colors hover:bg-background-50 ${
                  idx === 0 && pulseNew ? 'bg-accent-50/50' : ''
                }`}
              >
                <span className={`w-8 h-8 rounded-full flex items-center justify-center flex-shrink-0 ${eventColor(event.type)} text-white`}>
                  <i className={`${eventIcon(event.type)} text-sm`} />
                </span>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 flex-wrap">
                    <span className="text-xs font-semibold text-foreground-950">{event.title}</span>
                    {idx === 0 && pulseNew && (
                      <span className="px-1.5 py-0.5 rounded-full bg-accent-500 text-white text-[9px] font-bold uppercase animate-pulse">New</span>
                    )}
                  </div>
                  <p className="text-xs text-foreground-500 mt-0.5">{event.description}</p>
                  <div className="flex items-center gap-3 mt-1.5">
                    <span className="text-[10px] text-foreground-400">{event.actor}</span>
                    <span className="text-[10px] text-foreground-400">{getRelativeTime(event.timestamp)}</span>
                    {event.leadId && (
                      <span className="text-[10px] text-accent-700 cursor-pointer hover:underline" onClick={() => {
                        const lead = allLeads.find(l => l.id === event.leadId);
                        if (lead) {
                          setSelectedLead(lead);
                          setStatusNotes('');
                        }
                      }}>View lead <i className="ri-arrow-right-up-line text-[9px]" /></span>
                    )}
                    {event.propertyId && (
                      <span className="text-[10px] text-teal-600 cursor-pointer hover:underline" onClick={() => {
                        window.open('/dashboard/properties', '_self');
                      }}>View property <i className="ri-arrow-right-up-line text-[9px]" /></span>
                    )}
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Lead Detail Slide-out */}
      {selectedLead && (
        <div className="fixed inset-0 z-[1000] flex">
          <div className="absolute inset-0 bg-black/60 backdrop-blur-sm" onClick={() => setSelectedLead(null)} />
          <div className="relative z-10 ml-auto w-full max-w-xl bg-white h-full overflow-y-auto shadow-2xl">
            <div className="sticky top-0 bg-white border-b border-background-200/60 px-6 py-4 flex items-center justify-between z-10">
              <h2 className="font-heading text-lg font-bold text-foreground-950">Lead Detail</h2>
              <button onClick={() => setSelectedLead(null)} className="w-8 h-8 flex items-center justify-center rounded-lg hover:bg-background-200/60 text-foreground-500 cursor-pointer"><i className="ri-close-line text-lg" /></button>
            </div>
            <div className="p-6 space-y-5">
              <div>
                <div className="flex items-center gap-3 mb-3">
                  <div className="w-12 h-12 rounded-full bg-gradient-to-br from-accent-500 to-primary-500 flex items-center justify-center flex-shrink-0">
                    <span className="text-sm font-bold text-background-950">{selectedLead.name.split(' ').map(n => n[0]).join('')}</span>
                  </div>
                  <div>
                    <h3 className="text-lg font-semibold text-foreground-950">{selectedLead.name}</h3>
                    <span className={`text-[10px] font-semibold px-2 py-0.5 rounded-full border ${statusBadge(selectedLead.status)}`}>{stageLabels[selectedLead.status] || selectedLead.status}</span>
                  </div>
                </div>
                <div className="grid grid-cols-2 gap-4 mt-4">
                  <div><span className="text-[10px] text-foreground-500 uppercase tracking-wider block">Phone</span><a href={`tel:${selectedLead.phone}`} className="text-sm text-accent-700">{selectedLead.phone || '-'}</a></div>
                  <div><span className="text-[10px] text-foreground-500 uppercase tracking-wider block">Email</span><span className="text-sm text-foreground-950">{selectedLead.email}</span></div>
                  <div><span className="text-[10px] text-foreground-500 uppercase tracking-wider block">Source</span><span className="text-sm text-foreground-950 capitalize">{selectedLead.source || 'website'}</span></div>
                  <div><span className="text-[10px] text-foreground-500 uppercase tracking-wider block">Company</span><span className="text-sm text-foreground-950">{selectedLead.company || '-'}</span></div>
                </div>
                {selectedLead.listing_title && (
                  <div className="mt-4"><span className="text-[10px] text-foreground-500 uppercase tracking-wider block mb-1">Property Interest</span><span className="text-sm text-foreground-950">{selectedLead.listing_title}</span></div>
                )}
                <div className="mt-4"><span className="text-[10px] text-foreground-500 uppercase tracking-wider block mb-1">Subject</span><span className="text-sm text-foreground-950">{selectedLead.subject}</span></div>
                <div className="mt-4"><span className="text-[10px] text-foreground-500 uppercase tracking-wider block mb-1">Message</span><p className="text-sm text-foreground-500">{selectedLead.message}</p></div>
                {selectedLead.notes && (
                  <div className="mt-4"><span className="text-[10px] text-foreground-500 uppercase tracking-wider block mb-1">Notes</span><p className="text-sm text-foreground-500">{selectedLead.notes}</p></div>
                )}
                <div className="mt-4"><span className="text-[10px] text-foreground-500 uppercase tracking-wider block">Received</span><span className="text-sm text-foreground-950">{formatDate(selectedLead.created_at)}</span></div>
              </div>

              {/* Status Update Section */}
              <div className="border-t border-background-200/60 pt-5 space-y-4">
                <h3 className="text-xs font-semibold text-foreground-500 uppercase tracking-wider">Update Status</h3>
                <div className="flex flex-wrap gap-2">
                  {pipelineStages.map(stage => (
                    <button
                      key={stage}
                      onClick={() => handleStatusUpdate(selectedLead.id, stage)}
                      disabled={submitting || selectedLead.status === stage}
                      className={`px-3 py-1.5 rounded-lg text-[11px] font-semibold border transition-all cursor-pointer whitespace-nowrap ${
                        selectedLead.status === stage
                          ? 'bg-accent-100 text-accent-700 border-accent-200'
                          : 'border-background-200/60 text-foreground-500 hover:text-foreground-950 hover:border-accent-200 disabled:opacity-40'
                      }`}
                    >
                      {stageLabels[stage]}
                    </button>
                  ))}
                </div>
                <div>
                  <label className="text-[10px] font-semibold text-foreground-500 uppercase tracking-wider block mb-1.5">Add Note (optional)</label>
                  <textarea
                    rows={2}
                    placeholder="Optional note for this status change..."
                    value={statusNotes}
                    onChange={e => setStatusNotes(e.target.value)}
                    className="w-full px-4 py-2.5 rounded-xl bg-white border border-background-200/60 text-sm text-foreground-950 placeholder:text-foreground-400 focus:outline-none focus:border-accent-500/50 resize-none"
                  />
                </div>
              </div>

              {/* Danger Zone */}
              <div className="border-t border-background-200/60 pt-5">
                <button
                  onClick={() => setDeleteTarget(selectedLead)}
                  className="flex items-center gap-1.5 px-4 py-2 rounded-lg border border-rose-500/20 text-xs text-rose-400 hover:bg-rose-500/10 cursor-pointer whitespace-nowrap"
                >
                  <i className="ri-delete-bin-line" /> Delete Lead
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Delete Confirmation */}
      {deleteTarget && (
        <div className="fixed inset-0 z-[1100] flex items-center justify-center px-4">
          <div className="absolute inset-0 bg-black/60 backdrop-blur-sm" onClick={() => setDeleteTarget(null)} />
          <div className="relative z-10 w-full max-w-sm bg-white rounded-2xl shadow-2xl p-6 space-y-4">
            <div className="text-center">
              <div className="w-12 h-12 mx-auto rounded-full bg-rose-500/15 flex items-center justify-center mb-3">
                <i className="ri-delete-bin-line text-rose-400 text-xl" />
              </div>
              <h3 className="font-heading text-lg font-bold text-foreground-950">Delete Lead?</h3>
              <p className="text-sm text-foreground-500 mt-1">This will permanently delete <strong className="text-foreground-950">{deleteTarget.name}</strong>. This cannot be undone.</p>
            </div>
            <div className="flex gap-2">
              <button onClick={() => setDeleteTarget(null)} className="flex-1 py-2.5 rounded-xl border border-background-200/60 text-sm text-foreground-500 hover:text-foreground-950 cursor-pointer whitespace-nowrap">Cancel</button>
              <button onClick={handleDelete} disabled={submitting} className="flex-1 py-2.5 rounded-xl bg-rose-500 text-white text-sm font-semibold hover:bg-rose-600 disabled:opacity-50 cursor-pointer whitespace-nowrap">
                {submitting ? 'Deleting...' : 'Delete'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Create Lead Modal */}
      {showCreate && (
        <div className="fixed inset-0 z-[1000] flex items-center justify-center px-4">
          <div className="absolute inset-0 bg-black/60 backdrop-blur-sm" onClick={() => setShowCreate(false)} />
          <div className="relative z-10 w-full max-w-md bg-white rounded-2xl shadow-2xl p-6 space-y-4">
            <div className="flex items-center justify-between">
              <h2 className="font-heading text-lg font-bold text-foreground-950">Add Lead</h2>
              <button onClick={() => setShowCreate(false)} className="w-8 h-8 flex items-center justify-center rounded-lg hover:bg-background-200/60 text-foreground-500 cursor-pointer"><i className="ri-close-line" /></button>
            </div>
            <div className="space-y-3">
              <div>
                <label className="text-[10px] font-semibold text-foreground-500 uppercase tracking-wider block mb-1">Name *</label>
                <input type="text" placeholder="Full name" value={createForm.name} onChange={e => setCreateForm(f => ({ ...f, name: e.target.value }))} className="w-full px-4 py-2.5 rounded-xl bg-white border border-background-200/60 text-sm text-foreground-950" />
              </div>
              <div>
                <label className="text-[10px] font-semibold text-foreground-500 uppercase tracking-wider block mb-1">Email *</label>
                <input type="email" placeholder="Email address" value={createForm.email} onChange={e => setCreateForm(f => ({ ...f, email: e.target.value }))} className="w-full px-4 py-2.5 rounded-xl bg-white border border-background-200/60 text-sm text-foreground-950" />
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="text-[10px] font-semibold text-foreground-500 uppercase tracking-wider block mb-1">Phone</label>
                  <input type="text" placeholder="Phone" value={createForm.phone} onChange={e => setCreateForm(f => ({ ...f, phone: e.target.value }))} className="w-full px-4 py-2.5 rounded-xl bg-white border border-background-200/60 text-sm text-foreground-950" />
                </div>
                <div>
                  <label className="text-[10px] font-semibold text-foreground-500 uppercase tracking-wider block mb-1">Source</label>
                  <select value={createForm.source} onChange={e => setCreateForm(f => ({ ...f, source: e.target.value }))} className="w-full px-4 py-2.5 rounded-xl bg-white border border-background-200/60 text-sm text-foreground-950 cursor-pointer">
                    <option value="website">Website</option>
                    <option value="phone">Phone Call</option>
                    <option value="whatsapp">WhatsApp</option>
                    <option value="referral">Referral</option>
                    <option value="instagram">Instagram</option>
                    <option value="linkedin">LinkedIn</option>
                  </select>
                </div>
              </div>
              <div>
                <label className="text-[10px] font-semibold text-foreground-500 uppercase tracking-wider block mb-1">Property Interest</label>
                <input type="text" placeholder="Property title or type" value={createForm.listing_title} onChange={e => setCreateForm(f => ({ ...f, listing_title: e.target.value }))} className="w-full px-4 py-2.5 rounded-xl bg-white border border-background-200/60 text-sm text-foreground-950" />
              </div>
              <div>
                <label className="text-[10px] font-semibold text-foreground-500 uppercase tracking-wider block mb-1">Subject</label>
                <input type="text" placeholder="Lead subject" value={createForm.subject} onChange={e => setCreateForm(f => ({ ...f, subject: e.target.value }))} className="w-full px-4 py-2.5 rounded-xl bg-white border border-background-200/60 text-sm text-foreground-950" />
              </div>
              <div>
                <label className="text-[10px] font-semibold text-foreground-500 uppercase tracking-wider block mb-1">Message / Notes</label>
                <textarea rows={2} placeholder="Additional notes..." value={createForm.message} onChange={e => setCreateForm(f => ({ ...f, message: e.target.value }))} className="w-full px-4 py-2.5 rounded-xl bg-white border border-background-200/60 text-sm text-foreground-950 placeholder:text-foreground-400 focus:outline-none focus:border-accent-500/50 resize-none" />
              </div>
              <button onClick={handleCreateLead} disabled={submitting} className="w-full py-3 rounded-xl bg-accent-500 text-white text-sm font-semibold hover:bg-accent-400 disabled:opacity-50 cursor-pointer whitespace-nowrap">
                {submitting ? 'Saving...' : 'Save Lead'}
              </button>
            </div>
          </div>
        </div>
      )}
    </DashboardLayout>
  );
}